import { queryOptions, type QueryClient } from '@tanstack/react-query'

import { ensureRouteData } from '@/app/query-client'
import { brokerService } from '@/features/brokers/api/brokerService'
import { brokerKeys } from '@/features/brokers/api/keys'
import type { BusyNamesQuery } from '@/schemas/broker'

/**
 * The market-wide defaults the three param-less Broker Chirfaar routes fall
 * back to once `lastUsed.ts` comes back empty (a first-ever visit, or a
 * blocked/private store).
 *
 * Owner's ruling: the stock pages (By stock, PV range) default to the top
 * symbol of Most traded (`GET /api/v5/brokers/busy-names/`), and Broker's
 * Choice to `busiest_code` off Overview (`GET /api/v5/brokers/overview/`).
 * Both read through `ensureRouteData`, so a redirect loader's default lookup
 * behaves exactly like any other MAIN read — cache-first, offline-aware, and
 * a genuine failure propagates to the route's error page rather than
 * resolving to a bogus symbol.
 */

/**
 * Page 1, the app's smallest valid `PageSize` (`schemas/table.ts` closes the
 * set at 10/20/50/100/25 — there is no literal `1`): this call exists only
 * to name the busiest row, not to list any, and only `rows[0]` is read.
 */
const TOP_BUSY_NAME_QUERY: BusyNamesQuery = { page: 1, pageSize: 10, period: 'today' }

/**
 * Its own key, not `busyNamesQueryOptions`'s (`brokerKeys.busyNames`) or
 * `brokerOverviewQueryOptions`'s (`brokerKeys.overview`) below: those feed
 * Most traded and Overview their own screens' `data`, where an empty page or
 * a null `busiest` is a normal, RENDERABLE answer, not a failure. Sharing
 * either key here would mean a market with nothing traded yet also failing
 * THOSE screens' own queries, which is not this module's call to make. Not
 * added to `keys.ts`'s shared factory either — nothing outside this file ever
 * wants either key, unlike every branch that lives there.
 *
 * The "no rows"/"no busiest" check throws FROM INSIDE the queryFn rather than
 * after `ensureRouteData` has already resolved successfully — that is what
 * lets `RouteErrorPage`'s retry find this query at all. Its `retry` looks up
 * `queryCache.findAll({ predicate: q => q.state.error === error })`: only a
 * query that itself failed can match, so a plain `new Error(...)` thrown from
 * outside a queryFn holds no such query, and "Try again" fell through
 * straight to `router.load()` against the still-cached (still empty) answer
 * — the identical failure, replayed instantly, forever. Throwing inside the
 * queryFn makes this the query's OWN error, so retry finds it, forces a real
 * `refetchQueries` (not just a re-read of stale cache), and only re-runs the
 * redirect loader once that refetch actually produces a row.
 */
const defaultBrokerStockQueryOptions = () =>
  queryOptions<string>({
    queryKey: [...brokerKeys.all, 'default-stock'] as const,
    queryFn: async () => {
      const page = await brokerService.busyNames(TOP_BUSY_NAME_QUERY)
      const top = page.rows[0]
      if (top === undefined) {
        throw new Error('Most traded has no rows to default By stock or PV range to')
      }
      return top.symbol
    },
  })

/**
 * The top row of today's Most traded — By stock and PV range's shared
 * default. Rejects if the endpoint answers with no rows at all (a market with
 * literally nothing traded), which the caller lets become the route's error
 * page rather than redirecting nowhere — see `defaultBrokerStockQueryOptions`
 * for why that rejection is a real, retryable query failure now.
 */
export async function defaultBrokerStock(client: QueryClient): Promise<string> {
  return ensureRouteData(client, defaultBrokerStockQueryOptions())
}

const defaultBrokerQueryOptions = () =>
  queryOptions<string>({
    queryKey: [...brokerKeys.all, 'default-broker'] as const,
    queryFn: async () => {
      const overview = await brokerService.overview('today')
      if (overview.busiest === null) {
        throw new Error('Overview reported no busiest broker to default Broker’s Choice to')
      }
      return overview.busiest.code
    },
  })

/**
 * Today's busiest broker — Broker's Choice's default. Rejects when the
 * overview reports none (`busiest: null`, e.g. an empty precompute), the
 * same "no default, no redirect, but a retryable one" contract as
 * `defaultBrokerStock`.
 */
export async function defaultBroker(client: QueryClient): Promise<string> {
  return ensureRouteData(client, defaultBrokerQueryOptions())
}
